import { useEffect, useState } from 'react';
import { Flame } from 'lucide-react';
import { fetchAdminStreaks, type AdminStreak } from '@/lib/api';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './AdminList.module.css';

const STREAK_CODES = [
  { value: '', label: 'All streaks' },
  { value: 'weekly_charger', label: 'Weekly Charger' },
  { value: 'kwh_grinder', label: 'kWh Grinder' },
  { value: 'session_regular', label: 'Session Regular' },
  { value: 'explorer', label: 'Explorer' },
  { value: 'power_user', label: 'Power User' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Unenrolled' },
];

export function StreaksPage() {
  useRecentRoute('Streaks');
  const [rows, setRows] = useState<AdminStreak[]>([]);
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true); setError('');
    fetchAdminStreaks({
      ...(code ? { code } : {}),
      ...(status ? { status } : {}),
    })
      .then(setRows)
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [code, status]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Flame size={18} /></div>
          <div>
            <h1 className={styles.title}>Streaks</h1>
            <p className={styles.subtitle}>
              {rows.length} user-streak row{rows.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
      </div>

      <div className={styles.filterRow}>
        <select className={styles.select} value={code} onChange={(e) => setCode(e.target.value)}>
          {STREAK_CODES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className={styles.select} value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {loading && <p className={styles.loading}>Loading…</p>}

      {!loading && rows.length === 0 && (
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>No streaks match</p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Streak</th>
                <th>Current</th>
                <th>Longest</th>
                <th>Freeze offered</th>
                <th>Last eval</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div className={styles.userCell}>
                      <span className={styles.userName}>{r.userName}</span>
                      {r.userUsername && <span className={styles.userSub}>@{r.userUsername}</span>}
                    </div>
                  </td>
                  <td>
                    <code className={styles.cardCode}>{r.streakCode}</code>
                  </td>
                  <td>{r.currentWeekCount}w</td>
                  <td>{r.longestWeekCount}w</td>
                  <td>
                    {r.freezeOfferedAt
                      ? <span className={`${styles.badge} ${styles.badgePending}`}>Offered</span>
                      : '—'}
                  </td>
                  <td>{r.lastEvaluatedAt ? new Date(r.lastEvaluatedAt).toLocaleString() : '—'}</td>
                  <td>
                    <span className={`${styles.badge} ${r.active ? styles.badgeOk : styles.badgeMuted}`}>
                      {r.active ? 'Active' : 'Unenrolled'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
